import { createContext, useContext, useEffect, useState } from "react";
import askBenthos from "../services/apiChat";

const ChatContext = createContext();

/**
 * Provider component for the ChatProvider
 *
 * This component manages the states and functions related to questions and conversations.
 *
 * @param {React.ReactNode} children - The child components that will have access to the context
 */
function ChatProvider({ children }) {
  const [question, setQuestion] = useState("");
  const [loadingAnswer, setLoadingAnswer] = useState(false);
  const [conversation, setConversation] = useState([]);

  const [controller, setController] = useState(null);

  /**
   * Sends the question and starts processing the response
   *
   * @param {string} useCaseQuestion - A question originating from the Use Cases screen
   * @returns
   */
  async function ask({ useCaseQuestion } = {}) {
    if (!question && !useCaseQuestion) return;

    const newController = new AbortController();
    setController(newController);

    const currentQuestion = question || useCaseQuestion;

    const newQuestion = {
      type: "question",
      message: currentQuestion,
    };

    setConversation((prev) => [...prev, newQuestion]);
    setLoadingAnswer(true);

    let newResponse = {
      type: "response",
    };

    setQuestion("");

    try {
      const response = await askBenthos({
        question: currentQuestion,
        abort: newController.signal,
      });

      if (!response) return;

      newResponse = {
        ...newResponse,
        message: response,
      };

      setConversation((prev) => [...prev, { ...newResponse }]);
    } catch (error) {
      newResponse = {
        ...newResponse,
        message: "Something went wrong :(",
        error: true,
      };
    } finally {
      setLoadingAnswer(false);
    }
  }

  /**
   * Cancels the ongoing response processing
   */
  function cancelAnswer() {
    if (controller) {
      controller.abort();
      setController(null);
      setLoadingAnswer(false);
    }
  }

  /**
   * Handles the keydown event in the input field
   *
   * If the "Enter" key is pressed, it triggers the `ask()` function.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   * @returns
   */
  function handleKeyDown(e) {
    if (!question) return;

    if (e.key === "Enter") {
      ask();
    }
  }

  useEffect(() => {
    if (conversation.length > 0) {
      localStorage.setItem(
        "chat-dialog",
        JSON.stringify(
          conversation.map((message) => ({ ...message, read: true }))
        )
      );
    }
  }, [conversation]);

  return (
    <ChatContext.Provider
      value={{
        question,
        conversation,
        loadingAnswer,
        setQuestion,
        handleKeyDown,
        ask,
        cancelAnswer,
        setConversation,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
}

/**
 * Hook to access the QuestionContext
 *
 * @returns { object } The values and functions of the context
 */
function useChat() {
  const context = useContext(ChatContext);

  if (context === undefined)
    throw new Error("ChatContext was used outside of ChatProvider");

  return context;
}

export { useChat, ChatProvider };
